import { IonActionSheet, IonIcon, IonToggle } from '@ionic/react'
import Brightness4Icon from '@mui/icons-material/Brightness4'
import Brightness7Icon from '@mui/icons-material/Brightness7'
import FlashOnIcon from '@mui/icons-material/FlashOn'
import HomeIcon from '@mui/icons-material/Home'
import LightbulbIcon from '@mui/icons-material/Lightbulb'
import TvIcon from '@mui/icons-material/Tv'
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material'
import { $Enums, Object } from '@vae/server/dist/client'
import { bulb, chevronDown, close, flash, home, tv } from 'ionicons/icons'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { logEvent } from '../../../analytics/utils/firebaseUtils'
import { AppContext } from '../../../contexts/AppContext'
import useUser from '../../../contexts/hooks/useUser'
import { ObjectContext } from '../../../contexts/objectContext/ObjectContext'
import { backendCall } from '../../../modules/backendApi'
import { getLandingPageLabel } from '../../../modules/getLandingPageLabel'
import { trpc } from '../../../modules/trpc'
import PageWrapper from '../../../wrappers/pageWrapper/PageWrapper'

const AppSettings: React.FC = () => {
	const object = useContext(ObjectContext)
	const utils = trpc.useContext()
	const { isMobileView } = useContext(AppContext)

	const { user, setUser } = useUser()

	const { t, i18n } = useTranslation('pages_settings', { keyPrefix: 'appSettingsPage' })
	const { t: tc } = useTranslation('common')

	const [landingPageListOpen, setLandingPageListOpen] = useState(false)
	const [languageListOpen, setLanguageListOpen] = useState(false)
	const [themeListOpen, setThemeListOpen] = useState(false)
	const [mockObject, setMockObject] = useState(object)

	const [languageAnchorEl, setLanguageAnchorEl] = useState<null | HTMLElement>(null)
	const [themeAnchorEl, setThemeAnchorEl] = useState<null | HTMLElement>(null)
	const [landingPageAnchorEl, setLandingPageAnchorEl] = useState<null | HTMLElement>(null)

	const setObjectValue = async (column: keyof Object, value: boolean) => {
		if (!object.id) return
		setMockObject(state => ({ ...state, [column]: value }))
		await backendCall.edit('objects', object.id, { [column]: value })
		utils.user.getObjects.prefetch({ userId: user?.id })
	}

	const { data: settings } = trpc.user.userSettings.get.useQuery(undefined, {
		enabled: !!user?.id,
	})
	const editSettings = trpc.user.userSettings.edit.useMutation({
		onSuccess: () => utils.user.userSettings.invalidate(),
	})

	const handleThemeMode = useCallback((value: $Enums.ThemeMode) => {
		editSettings.mutate({ userId: user!.id, data: { themeMode: value } })
	}, [editSettings, user])

	useEffect(() => {
		if (settings && !settings.themeMode) {
			handleThemeMode('system')
		}
	}, [settings, handleThemeMode])

	if (!user) return null

	const getThemeModeLabel = (mode: $Enums.ThemeMode) => {
		switch (mode) {
			case 'dark':
				return t('dark')
			case 'light':
				return t('light')
			case 'system':
				return t('system')
		}
	}

	const handleMenuOpen =
		(setter: React.Dispatch<React.SetStateAction<null | HTMLElement>>) =>
			(event: React.MouseEvent<HTMLElement>) => {
				setter(event.currentTarget)
			}
	const handleMenuClose =
		(setter: React.Dispatch<React.SetStateAction<null | HTMLElement>>) => () => {
			setter(null)
			logEvent('click-generic-cancel')
		}

	return (
		<PageWrapper page='appSettings' appBarOptions={{ navigationType: 'back' }}>
			<div className='options'>
				<div className='flex justify-between items-center py-[0.7rem] px-4 h-[4.3rem] border-t border-text-grey-100 dark:border-brand-secondary-200-dark'>
					<label className='font-[550] text-text-dark dark:text-white'>{t('language')}</label>
					<div
						onClick={
							isMobileView ? () => setLanguageListOpen(true) : handleMenuOpen(setLanguageAnchorEl)
						}
						className='flex justify-between items-center py-[0.6rem] px-[0.85rem] rounded-[0.7rem] min-w-[11.4rem] hover dark:bg-[#031f22] bg-[#f5f5f5] text-brand-primary-100-light dark:text-brand-primary-100-dark border border-brand-primary-100-light dark:border-brand-primary-100-dark'
					>
						{i18n.language === 'fi' ? t('finnish') : t('english')}
						<IonIcon
							className='pl-[0.7rem] text-[1.4rem] text-brand-primary-100-light dark:text-brand-primary-100-dark'
							icon={chevronDown}
						/>
					</div>
				</div>
				{!isMobileView && (
					<Menu
						anchorEl={languageAnchorEl}
						open={Boolean(languageAnchorEl)}
						onClose={handleMenuClose(setLanguageAnchorEl)}
					>
						<MenuItem
							onClick={() => {
								logEvent('click-language-change', { value: 'fi' })
								i18n.changeLanguage('fi')
								setUser({ language: 'fi' })
								setLanguageAnchorEl(null)
							}}
						>
							<ListItemText>{t('finnish')}</ListItemText>
						</MenuItem>
						<MenuItem
							onClick={() => {
								logEvent('click-language-change', { value: 'en' })
								i18n.changeLanguage('en')
								setUser({ language: 'en' })
								setLanguageAnchorEl(null)
							}}
						>
							<ListItemText>{t('english')}</ListItemText>
						</MenuItem>
					</Menu>
				)}

				<div className='flex justify-between items-center py-[0.7rem] px-4 h-[4.3rem] border-t border-text-grey-100 dark:border-brand-secondary-200-dark'>
					<label className='font-[550] text-text-dark dark:text-white'>{t('theme')}</label>
					<div
						onClick={isMobileView ? () => setThemeListOpen(true) : handleMenuOpen(setThemeAnchorEl)}
						className='flex justify-between items-center py-[0.6rem] px-[0.85rem] rounded-[0.7rem] min-w-[11.4rem] hover dark:bg-[#031f22] bg-[#f5f5f5] text-brand-primary-100-light dark:text-brand-primary-100-dark border border-brand-primary-100-light dark:border-brand-primary-100-dark'
					>
						{settings?.themeMode
							? getThemeModeLabel(settings.themeMode)
							: getThemeModeLabel('system')}
						<IonIcon
							className='pl-[0.7rem] text-[1.4rem] text-brand-primary-100-light dark:text-brand-primary-100-dark'
							icon={chevronDown}
						/>
					</div>
				</div>
				{!isMobileView && (
					<Menu
						anchorEl={themeAnchorEl}
						open={Boolean(themeAnchorEl)}
						onClose={handleMenuClose(setThemeAnchorEl)}
					>
						<MenuItem
							onClick={() => {
								handleThemeMode('light')
								setThemeAnchorEl(null)
							}}
						>
							<ListItemIcon>
								<Brightness7Icon fontSize='small' />
							</ListItemIcon>
							<ListItemText>{t('light')}</ListItemText>
						</MenuItem>
						<MenuItem
							onClick={() => {
								handleThemeMode('dark')
								setThemeAnchorEl(null)
							}}
						>
							<ListItemIcon>
								<Brightness4Icon fontSize='small' />
							</ListItemIcon>
							<ListItemText>{t('dark')}</ListItemText>
						</MenuItem>
						<MenuItem
							onClick={() => {
								handleThemeMode('system')
								setThemeAnchorEl(null)
							}}
						>
							<ListItemText>{t('system')}</ListItemText>
						</MenuItem>
					</Menu>
				)}

				<div className='flex justify-between items-center py-[0.7rem] px-4 h-[4.3rem] border-t border-text-grey-100 dark:border-brand-secondary-200-dark'>
					<label className='font-[550] text-text-dark dark:text-white'>{t('homepage')}</label>
					<div
						onClick={
							isMobileView
								? () => setLandingPageListOpen(true)
								: handleMenuOpen(setLandingPageAnchorEl)
						}
						className='flex justify-between items-center py-[0.6rem] px-[0.85rem] rounded-[0.7rem] min-w-[11.4rem] hover dark:bg-[#031f22] bg-[#f5f5f5] text-brand-primary-100-light dark:text-brand-primary-100-dark border border-brand-primary-100-light dark:border-brand-primary-100-dark'
					>
						{user.landingPage === null ? t('overview') : getLandingPageLabel(user.landingPage)}
						<IonIcon
							className='pl-[0.7rem] text-[1.4rem] text-brand-primary-100-light dark:text-brand-primary-100-dark'
							icon={chevronDown}
						/>
					</div>
				</div>
				{!isMobileView && (
					<Menu
						anchorEl={landingPageAnchorEl}
						open={Boolean(landingPageAnchorEl)}
						onClose={handleMenuClose(setLandingPageAnchorEl)}
					>
						<MenuItem
							onClick={() => {
								setUser({ landingPage: 'overview' })
								setLandingPageAnchorEl(null)
							}}
						>
							<ListItemIcon>
								<HomeIcon fontSize='small' />
							</ListItemIcon>
							<ListItemText>{t('overview')}</ListItemText>
						</MenuItem>
						<MenuItem
							onClick={() => {
								setUser({ landingPage: 'consumption' })
								setLandingPageAnchorEl(null)
							}}
						>
							<ListItemIcon>
								<FlashOnIcon fontSize='small' />
							</ListItemIcon>
							<ListItemText>{t('myConsumption')}</ListItemText>
						</MenuItem>
						<MenuItem
							onClick={() => {
								setUser({ landingPage: 'spotMargin' })
								setLandingPageAnchorEl(null)
							}}
						>
							<ListItemIcon>
								<FlashOnIcon fontSize='small' />
							</ListItemIcon>
							<ListItemText>{t('exchangePrice')}</ListItemText>
						</MenuItem>
						<MenuItem
							onClick={() => {
								setUser({ landingPage: 'devices' })
								setLandingPageAnchorEl(null)
							}}
						>
							<ListItemIcon>
								<TvIcon fontSize='small' />
							</ListItemIcon>
							<ListItemText>{t('devices')}</ListItemText>
						</MenuItem>
						<MenuItem
							onClick={() => {
								setUser({ landingPage: 'saving' })
								setLandingPageAnchorEl(null)
							}}
						>
							<ListItemIcon>
								<LightbulbIcon fontSize='small' />
							</ListItemIcon>
							<ListItemText>{t('savings')}</ListItemText>
						</MenuItem>
					</Menu>
				)}

				{object.gsrn && object.synerallId && (
					<div className='flex justify-between items-center py-[0.7rem] px-4 h-[4.3rem] border-t border-text-grey-100 dark:border-brand-secondary-200-dark'>
						<label className='font-[550] text-text-dark dark:text-white'>{t('useDatahub')}</label>
						<IonToggle
							checked={mockObject.useDatahub}
							onIonChange={e => setObjectValue('useDatahub', e.detail.checked)}
							className='toggle-item [--track-background:#d6d6d6] dark:[--track-background:#09292b] [--handle-background:#a0a0a0] dark:[--handle-background:#063a3e] [--track-background-checked:#a8da9a] dark:[--track-background-checked:#0a5a5f] [--handle-background-checked:#52ae32] dark:[--handle-background-checked:#3cb49f]'
						/>
					</div>
				)}

				<div className='flex justify-between items-center py-[0.7rem] px-4 h-[4.3rem] border-y border-text-grey-100 dark:border-brand-secondary-200-dark'>
					<label className='font-[550] text-text-dark dark:text-white'>{t('monthlyFee')}</label>
					<IonToggle
						checked={mockObject.showMonthlyPrice}
						onIonChange={e => setObjectValue('showMonthlyPrice', e.detail.checked)}
						className='toggle-item [--track-background:#d6d6d6] dark:[--track-background:#09292b] [--handle-background:#a0a0a0] dark:[--handle-background:#063a3e] [--track-background-checked:#a8da9a] dark:[--track-background-checked:#0a5a5f] [--handle-background-checked:#52ae32] dark:[--handle-background-checked:#3cb49f]'
					/>
				</div>
			</div>

			<IonActionSheet
				isOpen={languageListOpen}
				onDidDismiss={() => setLanguageListOpen(false)}
				header={t('chooseLanguage')}
				buttons={[
					{
						text: t('finnish'),
						handler: () => {
							logEvent('click-language-change', { value: 'fi' })
							i18n.changeLanguage('fi')
							setUser({ language: 'fi' })
						},
					},
					{
						text: t('english'),
						handler: () => {
							logEvent('click-language-change', { value: 'en' })
							i18n.changeLanguage('en')
							setUser({ language: 'en' })
						},
					},
				]}
			/>

			<IonActionSheet
				isOpen={landingPageListOpen}
				onDidDismiss={() => setLandingPageListOpen(false)}
				header={t('chooseHomepage')}
				buttons={[
					{
						text: t('overview'),
						icon: home,
						handler: () => {
							setUser({ landingPage: 'overview' })
						},
					},
					{
						text: t('myConsumption'),
						icon: flash,
						handler: () => {
							setUser({ landingPage: 'consumption' })
						},
					},
					{
						text: t('exchangePrice'),
						icon: flash,
						handler: () => {
							setUser({ landingPage: 'spotMargin' })
						},
					},
					{
						text: t('devices'),
						icon: tv,
						handler: () => {
							setUser({ landingPage: 'devices' })
						},
					},
					{
						text: t('savings'),
						icon: bulb,
						handler: () => {
							setUser({ landingPage: 'saving' })
						},
					},
					{
						text: tc('peruuta'),
						role: 'cancel',
						icon: close,
						handler: () => {
							logEvent('click-generic-cancel')
						},
					},
				]}
			/>

			<IonActionSheet
				isOpen={themeListOpen}
				onDidDismiss={() => setThemeListOpen(false)}
				header={t('chooseTheme')}
				buttons={[
					{
						text: t('light'),
						handler: () => handleThemeMode('light'),
					},
					{
						text: t('dark'),
						handler: () => handleThemeMode('dark'),
					},
					{
						text: t('system'),
						handler: () => handleThemeMode('system'),
					},
					{
						text: tc('peruuta'),
						role: 'cancel',
						icon: close,
						handler: () => {
							logEvent('click-generic-cancel')
						},
					},
				]}
			/>
		</PageWrapper>
	)
}

export default AppSettings
